<template>
  <div id="map-container" />
</template>

<script>
import mapboxgl from 'mapbox-gl';

export default {
  name: 'MapboxMap',

  props: {
    tiles: {
      type: String,
      default: 'sales',
    },
  },

  data() {
    return {
      map: null,
      colorNone: '#c0c0c0',
      colorRankSteps: [
        'step',
        ['feature-state', 'rank'],
        '#c0c0c0', 0,
        '#28caf4', 150000,
        '#377bf4', 500000,
        '#311df4', 1000000,
        '#8104f4', 5000000,
        '#c804f4',
      ],
      colorZoningCategories: [
        'match',
        ['get', 'zoning'],
        '', '#c0c0c0',
        'residential', '#377bf4',
        'residential-high', '#311df4',
        'commercial', '#8104f4',
        'commercial-high', '#c804f4',
        'industrial', '#28caf4',
        '#c0c0c0',
      ],
      colorPermitsAlterSteps: [
        'step',
        ['get', 'alter_permits'],
        '#c0c0c0',
        1, '#28caf4',
        2, '#8104f4',
      ],
      colorPermitsConstSteps: [
        'step',
        ['get', 'const_permits'],
        '#c0c0c0',
        1, '#28caf4',
        2, '#8104f4',
      ],
    };
  },

  watch: {
    // eslint-disable-next-line no-unused-vars
    tiles(newTiles, oldTiles) {
      this.showTiles(newTiles);
    },
  },

  mounted() {
    mapboxgl.accessToken = 'pk.eyJ1IjoibGV0c3BsYW4iLCJhIjoiY2trY3phaHMxMDVnZzJubzV1cjdkOGZhaSJ9.cnIlJqWhotB0yniunP2Z6w';

    // push to end of call stack to avoid layout race
    setTimeout(this.initMap, 0);
  },

  methods: {
    getLayerSteps() {
      const { devIndex, presIndex } = this.$store.getters.getField('layers');

      let max = 100;
      let attribute;

      if (presIndex && devIndex) { // both
        max = 200;
        attribute = 'combined_layers';
      } else if (presIndex) {
        attribute = 'presIndex';
      } else if (devIndex) {
        attribute = 'devIndex';
      } else { // neither
        return this.colorNone;
      }

      return [
        'step',
        ['feature-state', attribute],
        '#c0c0c0', 0,
        '#28caf4', 0.2 * max,
        '#377bf4', 0.4 * max,
        '#311df4', 0.6 * max,
        '#8104f4', 0.8 * max,
        '#c804f4',
      ];
    },
    highlightSource(feature) {
      const clickHighlightSource = this.map.getSource('click-highlight');
      clickHighlightSource.setData(feature);
    },
    highlightClear() {
      const clickHighlightSource = this.map.getSource('click-highlight');
      const emptyPolygonFeature = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [],
        },
      };
      clickHighlightSource.setData(emptyPolygonFeature);
    },

    showTiles(tiles) {
      this.$emit('parcel-rank-changed', tiles);
      if (tiles === 'sales') {
        this.map.setPaintProperty('urban-areas-fill', 'fill-color', this.colorRankSteps);
        return;
      }
      if (tiles === 'zoning') {
        this.map.setPaintProperty('urban-areas-fill', 'fill-color', this.colorZoningCategories);
        return;
      }
      if (tiles === 'alteration') {
        this.map.setPaintProperty('urban-areas-fill', 'fill-color', this.colorPermitsAlterSteps);
        return;
      }
      if (tiles === 'construction') {
        this.map.setPaintProperty('urban-areas-fill', 'fill-color', this.colorPermitsConstSteps);
        return;
      }
      if (tiles === 'layers') {
        this.getLayerSteps();
        this.map.setPaintProperty('urban-areas-fill', 'fill-color', this.getLayerSteps());
        return;
      }
      this.map.setPaintProperty('urban-areas-fill', 'fill-color', this.colorNone);
    },

    initMap() {
      const parcelsTileUrl = process.env.MIX_MBTILE_URL ? process.env.MIX_MBTILE_URL : `${window.location.origin}/urban/{z}/{x}/{y}.pbf`;
      const parcelsTileHostname = new URL(parcelsTileUrl).hostname;
      const parcelsTileUrlRegex = new RegExp(`^https?://${parcelsTileHostname}`, 'i');

      const emptyPolygonFeature = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [],
        },
      };

      this.map = new mapboxgl.Map({
        container: 'map-container',
        style: 'mapbox://styles/mapbox/dark-v10?optimize=true',
        center: [-75.207138, 39.950872],
        zoom: 14,
        transformRequest: (url) => ({
          url,
          // allow cross-origin credentials for parcels tiles:
          credentials: parcelsTileUrlRegex.test(url) ? 'include' : 'same-origin',
        }),
      });

      this.map
        .addControl(new mapboxgl.NavigationControl({ visalizePitch: true }))
        .addControl(new mapboxgl.GeolocateControl())
        .addControl(new mapboxgl.ScaleControl());

      const { map } = this;
      map.on('load', () => {
        // Find the ID of the first symbol layer in the map style
        const { layers } = map.getStyle();
        let targetLayerId;
        for (let i = 0; i < layers.length; i += 1) {
          // if (layers[i].type === 'symbol') {
          if (layers[i].id === 'building') {
            // grab the next layer
            targetLayerId = layers[i + 1].id;
            break;
          }
        }
        map
          .addSource('urban-areas', {
            name: 'urban-areas',
            type: 'vector',
            generateId: true,
            promoteId: 'parcel_id',
            tiles: [parcelsTileUrl],
            maxzoom: 14, // max zoom compiled into the mbtiles file
          });

        const colorNone = '#c0c0c0';
        map
          .addLayer(
            {
              id: 'urban-areas-fill',
              type: 'fill',
              source: 'urban-areas',
              'source-layer': 'urban-areas',
              minzoom: 7, // min zoom to display
              maxzoom: 22, // max zoom to display
              layout: {},
              paint: {
                'fill-color': colorNone,
                'fill-opacity': 0.4,
              },
            },
            // Insert the layer beneath the first symbol layer.
            targetLayerId,
          );

        // add empty source to highlight clicked parcels
        map.addSource('click-highlight', {
          type: 'geojson',
          data: emptyPolygonFeature,
        });
        map.addLayer({
          id: 'click-highlight',
          type: 'line',
          source: 'click-highlight',
          layout: {},
          paint: {
            'line-color': '#f08',
            'line-width': 3,
          },
        });

        // pass events up to vue
        map.on('sourcedata', (e) => {
          // sourceDataType == 'visibility' for events that aren't loading events
          if (e.isSourceLoaded && !e.sourceDataType) {
            this.$emit('source-data-loaded', e);
          }
        });

        map.on('click', 'urban-areas-fill', (e) => {
          const coordinates = e.lngLat;
          const parcel = e.features[0].properties;
          const feature = e.features[0];
          // Ensure that if the map is zoomed out such that multiple
          // copies of the feature are visible, the popup appears
          // over the copy being pointed to.
          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }
          this.$emit('parcel-click', { properties: parcel, coords: coordinates, feature });
        });

        this.showTiles(this.tiles);
        this.$store.subscribe((mutation) => {
          // re-render map overlay if layers was changed
          if (mutation.type === 'updateLayers') {
            this.showTiles(this.tiles);
          }
        });
      });
    },
  },
};
</script>

<style lang="scss">
  #map-container {
    height: 100%;
  }

  .mapboxgl-map {
    font: inherit;
  }

  .mapboxgl-popup {
    font-size: .875rem;
    line-height: (4/3);

    .popup__title {
      font-size: 1rem;

      small {
        font-weight: normal;
      }
    }

    .popup__subtitle {
      font-size: .75rem;
      font-weight: normal;
      margin-bottom: .25rem;
    }

    .figure-group {
      border-top: 1px solid rgba(black, .1);
      display: flex;
      padding: .25rem 0;

      &.-scores {
        display: grid;
        grid-gap: .25rem 1rem;
        grid-template-columns: 1fr 1fr;

        .figure {
          margin: 0;
        }

        .figure__content {
          font-size: 1.25rem;
          font-weight: normal;
          letter-spacing: -.05em;
          line-height: 1.125;
        }
      }
    }

    .figure {
      + .figure {
        margin-left: 1rem;
      }
    }

    .figure__description {
      font-size: .75rem;
    }

    .figure__content {
      font-weight: bold;
    }
  }

  .mapboxgl-popup-content {
    padding: .5rem .75rem;
  }

  .mapboxgl-popup-close-button {
    padding: 0 .375rem;
  }

  // account for collapsed bottom sheet
  .mapboxgl-ctrl-bottom-left,
  .mapboxgl-ctrl-bottom-right {
    bottom: 96px;
  }
</style>
